import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { api } from '../../utils/api';
import ReactQuill from 'react-quill-new';
import 'react-quill/dist/quill.snow.css';
import { Save, Shield, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export const AdminLegal = () => {
  const [privacy, setPrivacy] = useState('');
  const [terms, setTerms] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchLegal = async () => {
      try {
        const res = await api.legal.getAll();
        const priv = res.data.find(l => l.id === 'privacy_policy');
        const term = res.data.find(l => l.id === 'terms_of_use');
        if (priv) setPrivacy(priv.content);
        if (term) setTerms(term.content);
      } catch (error) {
        console.error('Error fetching legal:', error);
        toast.error('Error al cargar contenidos legales');
      } finally {
        setLoading(false);
      }
    };
    fetchLegal();
  }, []);

  const handleSave = async (id, content) => {
    setSaving(true);
    try {
      await api.legal.update(id, content);
      toast.success('Contenido actualizado correctamente');
    } catch (error) {
      console.error('Error updating legal:', error);
      toast.error('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C6FF00]"></div>
        </div>
      </AdminLayout>
    );
  }

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  return (
    <AdminLayout>
      <div className="space-y-12">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <span className="text-[#C6FF00] font-black uppercase tracking-[0.3em] text-[10px] block mb-2">Configuración</span>
            <h1 className="text-4xl font-black tracking-tighter uppercase italic">Gestión Legal</h1>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Privacy Policy */}
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#C6FF00]/10 border border-[#C6FF00]/20 flex items-center justify-center text-[#C6FF00]">
                  <Shield size={20} />
                </div>
                <h2 className="text-xl font-black uppercase tracking-tight">Política de Privacidad</h2>
              </div>
              <button
                onClick={() => handleSave('privacy_policy', privacy)}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-[#C6FF00] text-black font-black uppercase tracking-widest text-[10px] rounded-full hover:scale-105 transition-all disabled:opacity-50"
              >
                <Save size={14} />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
            <div className="rounded-3xl border border-white/5 bg-white/[0.02] overflow-hidden min-h-[400px]">
              <ReactQuill
                theme="snow"
                value={privacy}
                onChange={setPrivacy}
                modules={quillModules}
                className="quill-dark"
              />
            </div>
          </section>

          {/* Terms of Use */}
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#C6FF00]/10 border border-[#C6FF00]/20 flex items-center justify-center text-[#C6FF00]">
                  <FileText size={20} />
                </div>
                <h2 className="text-xl font-black uppercase tracking-tight">Términos de Uso</h2>
              </div>
              <button
                onClick={() => handleSave('terms_of_use', terms)}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-[#C6FF00] text-black font-black uppercase tracking-widest text-[10px] rounded-full hover:scale-105 transition-all disabled:opacity-50"
              >
                <Save size={14} />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
            <div className="rounded-3xl border border-white/5 bg-white/[0.02] overflow-hidden min-h-[400px]">
              <ReactQuill
                theme="snow"
                value={terms}
                onChange={setTerms}
                modules={quillModules}
                className="quill-dark"
              />
            </div>
          </section>
        </div>

        <div className="p-8 rounded-[2rem] border border-[#C6FF00]/20 bg-[#C6FF00]/5 flex items-start gap-6">
          <AlertCircle className="text-[#C6FF00] shrink-0" size={24} />
          <div>
            <h4 className="text-white font-black uppercase tracking-widest text-[10px] mb-2">Aviso del Sistema</h4>
            <p className="text-white/40 text-sm font-medium">
              Estos contenidos son fundamentales para el cumplimiento legal. Asegúrese de que la información sea precisa y esté actualizada periódicamente según las regulaciones vigentes.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .quill-dark .ql-toolbar {
          background: rgba(255, 255, 255, 0.05);
          border: none !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
          padding: 1rem !important;
        }
        .quill-dark .ql-container {
          background: transparent;
          border: none !important;
          color: white;
          font-family: inherit;
          min-height: 350px;
        }
        .quill-dark .ql-editor {
          padding: 2rem !important;
          font-size: 1rem;
        }
        .quill-dark .ql-stroke { stroke: rgba(255, 255, 255, 0.5) !important; }
        .quill-dark .ql-fill { fill: rgba(255, 255, 255, 0.5) !important; }
        .quill-dark .ql-picker { color: rgba(255, 255, 255, 0.5) !important; }
        .quill-dark .ql-picker-options { background: #0A0A0A !important; border: 1px solid rgba(255, 255, 255, 0.1) !important; }
        .quill-dark .ql-active .ql-stroke { stroke: #C6FF00 !important; }
        .quill-dark .ql-active { color: #C6FF00 !important; }
      `}</style>
    </AdminLayout>
  );
};
